import { useEffect, useState } from 'react';
import {
  Input, Button, Divider, Segmented, AutoComplete,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import CategoryFilter from './CategoryFilter';
import CategoryProvider from '../../Home/HowMuchToTip/CategoryProvider';
import { IconFilter, IconTravelCase } from '../../../Common/Icon';
import PostList from '../../../Common/PostList';
import { PostListParams } from '../../../../store/post/list';
import {
  City, Country, State, useCity, useCountries, useStates,
} from '../../../../hooks/api/posts';
import ModalClaimBusinessTwo from '../../../Common/ModalClaimBusinessTwo';

import styles from './index.module.scss';

const USCountryObj = {
  id: 233,
  name: 'United States of America',
  iso3: 'USA',
};

function BusinessSearchPage() {
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState(USCountryObj.name);
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [category, setCategory] = useState('');
  const [resetCategoryTree, setResetCategoryTree] = useState(false);
  const [countryAutocomplete, setCountryAutoComplete] = useState<Country[]>([]);
  const [cityAutocomplete, setCityAutoComplete] = useState<City[]>([]);
  const [stateAutoComplete, setStateAutoComplete] = useState<State[]>([]);
  const countriesData = useCountries();
  const stateData = useStates();
  const cityData = useCity();

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (country) {
        countriesData.fetch({ search: country }).then((res) => {
          setCountryAutoComplete(res?.data || []);
        });
      }
      if (state) {
        const params = {
          search: state,
        };

        if (country && (countryAutocomplete?.length || 0) === 1 && countryAutocomplete[0].id) {
          Object.assign(params, { country: countryAutocomplete[0].id });
        }
        stateData.fetch(params).then((res) => {
          setStateAutoComplete(res?.data || []);
        });
      }
      if (city) {
        const params = {
          search: city,
        };

        if (state && (stateAutoComplete?.length || 0) === 1 && stateAutoComplete[0].id) {
          Object.assign(params, { state: stateAutoComplete[0].id });
        }
        cityData.fetch(params).then((res) => {
          setCityAutoComplete(res?.data || []);
        });
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [state, city, country]);

  useEffect(() => {
    if (zipCode) {
      setFilter((prev) => ({
        ...prev,
        businessZipCode: zipCode,
      }));
      if (zipCode === '') {
        clearFilters();
      }
    }
  }, [zipCode]);

  const [filter, setFilter] = useState<Partial<PostListParams>>({
    orderBy: 'DESC',
    orderByColumn: 'createdAt',
    status: 'published',
    categoryId: undefined,
    subCategoryId: undefined,
    businessName: undefined,
    businessCity: undefined,
    businessState: undefined,
    businessCountry: 'United States of America',
    businessZipCode: undefined,
  });

  const handleSearch = () => {
    setFilter((prev) => ({
      ...prev,
      businessName: search,
    }));
    if (search) {
      setFilter((prev) => ({
        ...prev,
        businessName: search,
      }));
    }
  };

  const clearFilters = () => {
    setFilter({
      businessId: undefined,
      orderBy: 'DESC',
      orderByColumn: 'createdAt',
      status: 'published',
      categoryId: undefined,
      subCategoryId: undefined,
      businessCity: undefined,
      businessCountry: USCountryObj.name,
      businessZipCode: undefined,
      businessState: undefined,
    });
    setCountry(USCountryObj.name);
    setCity('');
    setState('');
    setZipCode('');
    setCategory('');
    setSearch('');
    setResetCategoryTree(true);

    setTimeout(() => setResetCategoryTree(false), 0);
  };

  /** Debounce search */
  useEffect(() => {
    if (search.length >= 2) {
      const timeoutId = setTimeout(handleSearch, 700);

      return () => clearTimeout(timeoutId);
    }
    if (search.length === 0 && !stateData.loading) {
      handleSearch();
    }

    return undefined;
  }, [search]);

  const [openModal, setOpenModal] = useState<boolean>(false);

  return (
    <>
      <div className={styles.pageWrapper}>
        <div className={styles.filterTitle}>
          <span>
            <strong>Search for a business</strong> to view what other customers
            have to say about their tipping experiences
          </span>
          <Button
            type="primary"
            size="large"
            icon={<IconTravelCase />}
            className={styles.claimBtn}
            onClick={() => setOpenModal(true)}
          >
            Claim Your Business
          </Button>
        </div>
        <Input
          placeholder="Find Business"
          suffix={<SearchOutlined />}
          size="large"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={handleSearch}
          className={styles.searchInput}
        />
        <div className={styles.filterPanel}>
          <div className={styles.filterHeader}>
            <div>
              <IconFilter />
              <span>Filter by:</span>
            </div>
            <Button onClick={clearFilters} className={styles.clearButton}>
              Clear All
            </Button>
          </div>
          <Divider style={{ margin: '10px 0 20px' }} />
          <Segmented
            size="large"
            options={['Most Recent', 'Most Popular']}
            block
            onChange={(value) => {
              setFilter((prev) => ({
                ...prev,
                orderByColumn:
                  value === 'Most Popular' ? 'numberOfLikes' : 'createdAt',
              }));
            }}
          />
          <h5>Country</h5>
          <AutoComplete
            options={countryAutocomplete.map((item) => ({ value: item.name }))}
            value={country}
            onChange={(value) => setCountry(value)}
            placeholder="Country"
            className={styles.filterInput}
            style={{ border: 'none', borderRadius: 10 }}
            onSelect={(value) => {
              setFilter((prev) => ({
                ...prev,
                businessCountry: value,
              }));
            }}
            onSearch={(value) => {
              if (value.length === 0) {
                setState('');
              }
              setCountry(value);
            }}
          />
          <h5>State</h5>
          <AutoComplete
            options={stateAutoComplete.map((item) => ({ value: item.name }))}
            value={state}
            onChange={(value) => setState(value)}
            placeholder="State"
            className={styles.filterInput}
            style={{ border: 'none', borderRadius: 10 }}
            onSelect={(value) => {
              setFilter((prev) => ({
                ...prev,
                businessState: value,
              }));
            }}
            onSearch={(value) => {
              if (value.length === 0) {
                setCity('');
              }
              setState(value);
            }}
          />
          <h5>City</h5>
          <AutoComplete
            options={cityAutocomplete.map((item) => ({ value: item.name }))}
            value={city}
            onChange={(value) => {
              setCity(value);
            }}
            onSelect={(value) => {
              setFilter((prev) => ({
                ...prev,
                businessCity: value,
              }));
            }}
            placeholder="City"
            className={styles.filterInput}
            style={{ border: 'none', borderRadius: 10 }}
            onSearch={(value) => {
              setCity(value);
            }}
          />
          <h5>Zip Code</h5>
          <Input
            placeholder="Zip Code"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            className={styles.filterInput}
          />
          <CategoryProvider>
            <CategoryFilter setFilter={setFilter} reset={resetCategoryTree} />
          </CategoryProvider>
        </div>

        <div className={styles.searchArea}>
          <PostList filter={filter} isHeader />
        </div>
      </div>
      <ModalClaimBusinessTwo
        isOpen={openModal}
        onCancel={() => setOpenModal(false)}
      />
    </>

  );
}

export default BusinessSearchPage;
